import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Info } from 'lucide-react';

const InterestCalculatorTool = () => {
  const [mode, setMode] = useState('simple');
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [time, setTime] = useState('');
  const [timeUnit, setTimeUnit] = useState('years');
  const [compoundFrequency, setCompoundFrequency] = useState('12');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const P = parseFloat(principal);
    const R = parseFloat(rate) / 100;
    let T = parseFloat(time);

    if (isNaN(P) || isNaN(R) || isNaN(T) || P <= 0 || R < 0 || T <= 0) {
      setResult(null);
      return;
    }

    // Convert time to years
    if (timeUnit === 'months') {
      T = T / 12;
    } else if (timeUnit === 'days') {
      T = T / 365;
    }

    let totalInterest, finalAmount;

    if (mode === 'simple') {
      // Simple Interest: I = P × R × T
      totalInterest = P * R * T;
      finalAmount = P + totalInterest;
    } else {
      // Compound Interest: A = P(1 + r/n)^(nt)
      const n = parseFloat(compoundFrequency);
      finalAmount = P * Math.pow(1 + R / n, n * T);
      totalInterest = finalAmount - P;
    }

    setResult({
      mode,
      principal: P,
      rate: parseFloat(rate),
      time: parseFloat(time),
      timeUnit,
      timeInYears: T,
      compoundFrequency: mode === 'compound' ? parseFloat(compoundFrequency) : null,
      totalInterest,
      finalAmount,
      interestRatio: (totalInterest / P) * 100
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setPrincipal('');
    setRate('');
    setTime('');
    setTimeUnit('years');
    setCompoundFrequency('12');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getCompoundLabel = (freq) => {
    const labels = {
      '1': 'Annually',
      '2': 'Semi-annually',
      '4': 'Quarterly',
      '12': 'Monthly',
      '52': 'Weekly',
      '365': 'Daily'
    };
    return labels[freq] || freq + ' times/year';
  };

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <label className="block text-sm font-medium text-gray-300 mb-3">Interest Type</label>
        <div className="flex gap-2">
          <button
            onClick={() => setMode('simple')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
              mode === 'simple'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            data-testid="simple-mode-btn"
          >
            Simple Interest
          </button>
          <button
            onClick={() => setMode('compound')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
              mode === 'compound'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            data-testid="compound-mode-btn"
          >
            Compound Interest
          </button>
        </div>
      </div>

      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Principal Amount ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="principal-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g., 5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="rate-input"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Time Period
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.1"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="e.g., 5"
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                data-testid="time-input"
              />
              <select
                value={timeUnit}
                onChange={(e) => setTimeUnit(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                data-testid="time-unit-select"
              >
                <option value="years">Years</option>
                <option value="months">Months</option>
                <option value="days">Days</option>
              </select>
            </div>
          </div>

          {mode === 'compound' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Compounding Frequency
              </label>
              <select
                value={compoundFrequency}
                onChange={(e) => setCompoundFrequency(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                data-testid="compound-frequency-select"
              >
                <option value="1">Annually (1/year)</option>
                <option value="2">Semi-annually (2/year)</option>
                <option value="4">Quarterly (4/year)</option>
                <option value="12">Monthly (12/year)</option>
                <option value="52">Weekly (52/year)</option>
                <option value="365">Daily (365/year)</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-interest-btn"
      >
        <Calculator size={18} />
        Calculate Interest
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="interest-results">
          {/* Main Results */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Interest Earned</span>
                <button
                  onClick={() => copyResult(result.totalInterest, 'interest')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'interest' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-interest"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="total-interest-result">
                {formatCurrency(result.totalInterest)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {result.interestRatio.toFixed(2)}% of principal
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Final Amount</span>
                <button
                  onClick={() => copyResult(result.finalAmount, 'final')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'final' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-final"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-blue-400" data-testid="final-amount-result">
                {formatCurrency(result.finalAmount)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Principal + Interest
              </div>
            </div>
          </div>

          {/* Calculation Summary */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator size={16} className="text-blue-400" />
              <h4 className="text-sm font-medium text-gray-300">Calculation Summary</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest Type</span>
                <span className="text-white font-medium capitalize">{result.mode} Interest</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Principal (P)</span>
                <span className="text-white font-medium">{formatCurrency(result.principal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Rate (R)</span>
                <span className="text-white font-medium">{result.rate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Time (T)</span>
                <span className="text-white font-medium">
                  {result.time} {result.timeUnit}
                  {result.timeUnit !== 'years' && ` (${result.timeInYears.toFixed(4)} years)`}
                </span>
              </div>
              {result.compoundFrequency && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Compounding (n)</span>
                  <span className="text-white font-medium">{getCompoundLabel(result.compoundFrequency.toString())}</span>
                </div>
              )}
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Total Interest</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.totalInterest)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Final Amount</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.finalAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Explanation */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">Formulas Explained</h3>
        </div>

        <div className="space-y-4">
          {/* Simple Interest */}
          <div className={`p-3 rounded-lg ${mode === 'simple' ? 'bg-blue-900/30 border border-blue-700' : 'bg-gray-900'}`}>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Simple Interest</h4>
            <div className="font-mono text-sm text-yellow-400 bg-gray-950 p-2 rounded mb-2">
              I = P × R × T
            </div>
            <div className="text-xs text-gray-400 space-y-1">
              <div><span className="text-gray-300">I</span> = Interest earned</div>
              <div><span className="text-gray-300">P</span> = Principal (initial amount)</div>
              <div><span className="text-gray-300">R</span> = Annual interest rate (as decimal)</div>
              <div><span className="text-gray-300">T</span> = Time in years</div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Interest is calculated only on the original principal. No compounding occurs.
            </div>
          </div>

          {/* Compound Interest */}
          <div className={`p-3 rounded-lg ${mode === 'compound' ? 'bg-blue-900/30 border border-blue-700' : 'bg-gray-900'}`}>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Compound Interest</h4>
            <div className="font-mono text-sm text-yellow-400 bg-gray-950 p-2 rounded mb-2">
              A = P × (1 + r/n)^(n×t)
            </div>
            <div className="text-xs text-gray-400 space-y-1">
              <div><span className="text-gray-300">A</span> = Final amount</div>
              <div><span className="text-gray-300">P</span> = Principal (initial amount)</div>
              <div><span className="text-gray-300">r</span> = Annual interest rate (as decimal)</div>
              <div><span className="text-gray-300">n</span> = Number of times compounded per year</div>
              <div><span className="text-gray-300">t</span> = Time in years</div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Interest is calculated on principal plus previously earned interest. More frequent compounding = more growth.
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Note */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Simple vs Compound Interest</h3>
        <div className="text-sm text-gray-400 space-y-2">
          <p>
            <span className="text-white">Simple Interest:</span> Calculated only on the original principal. 
            Best for short-term loans or when you're the borrower.
          </p>
          <p>
            <span className="text-white">Compound Interest:</span> Calculated on principal + accumulated interest. 
            Better for savings and investments due to exponential growth over time.
          </p>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-interest-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default InterestCalculatorTool;
